import axios from "axios";
import { DateTime } from "luxon";
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Modal, Input } from "antd";
import styled from "styled-components";
import { BbotButton, BbotTable, Tag, notification } from "top-component-library";
import { capitalizeString, formatPrice, generalErrorAlert } from "../../util/Utils";

export const CATERING_DRAFT_ORDERS_ENDPOINT = "/owner/catering-draft-orders";
export const HIGHLIGHT_ROW_CLASS = "catering-draft-row--highlighted";
const CATERING_DRAFT_ORDER_STATUS = {
  PENDING: "pending",
  ACCEPTED: "accepted",
  REJECTED: "rejected",
  CANCELLED: "cancelled",
  FINALIZING: "finalizing",
  FINALIZED: "finalized",
};
const MAX_REJECT_REASON_LENGTH = 500; // Must match Order.draft_rejected_reason max_length in backend models/orders.py
export const AUTO_REFRESH_INTERVAL_MS = 60000;

// NOTE: Notification wording also exists in backend catering/notifications.py
const ACTION_FORMS = {
  accept: { past: "accepted", gerund: "accepting" },
  reject: { past: "rejected", gerund: "rejecting" },
};

export const fulfillmentDateSorter = (a, b) => {
  const dateA = a.fulfillment_date ? new Date(a.fulfillment_date) : null;
  const dateB = b.fulfillment_date ? new Date(b.fulfillment_date) : null;
  if (!dateA && !dateB) return 0;
  if (!dateA) return 1;
  if (!dateB) return -1;
  return dateA - dateB;
};

const CATERING_DRAFT_ORDER_STATUS_COLOR_MAP = {
  [CATERING_DRAFT_ORDER_STATUS.PENDING]: "orange",
  [CATERING_DRAFT_ORDER_STATUS.ACCEPTED]: "green",
  [CATERING_DRAFT_ORDER_STATUS.REJECTED]: "red",
  [CATERING_DRAFT_ORDER_STATUS.CANCELLED]: "default",
  [CATERING_DRAFT_ORDER_STATUS.FINALIZING]: "cyan",
  [CATERING_DRAFT_ORDER_STATUS.FINALIZED]: "blue",
};

const getStatusColor = (status) => {
  if (status && !CATERING_DRAFT_ORDER_STATUS_COLOR_MAP[status]) {
    console.warn("Unmapped catering draft status:", status);
  }
  return CATERING_DRAFT_ORDER_STATUS_COLOR_MAP[status] || "default";
};

function buildColumns({ renderDate, activeAction, onAccept, onReject }) {
  return [
    {
      title: "Order #",
      dataIndex: "orderNumber",
      key: "orderNumber",
    },
    {
      title: "Customer",
      dataIndex: "patron_name",
      key: "patron_name",
      render: (text) => text || "Guest",
    },
    {
      title: "Fulfillment Date",
      dataIndex: "fulfillment_date",
      key: "fulfillment_date",
      render: renderDate,
      sorter: fulfillmentDateSorter,
    },
    {
      title: "Lock Date",
      dataIndex: "lock_date",
      key: "lock_date",
      render: renderDate,
    },
    {
      title: "Status",
      dataIndex: "draft_status",
      key: "draft_status",
      render: (status, record) => {
        const label = status ? capitalizeString(status) : "\u2014";
        const rejecter = status === CATERING_DRAFT_ORDER_STATUS.REJECTED ? record.rejected_by_name : null;
        return (
          <StatusCell>
            <Tag color={getStatusColor(status)}>{label}</Tag>
            {rejecter && <RejecterLabel>by {rejecter}</RejecterLabel>}
          </StatusCell>
        );
      },
    },
    {
      title: "Method",
      dataIndex: "catering_method",
      key: "catering_method",
      render: (method) => {
        if (method === "pickup") return "Pickup";
        if (method === "delivery") return "Delivery";
        return "—";
      },
    },
    {
      title: "Items",
      dataIndex: "items",
      key: "items",
      render: (items) => (Array.isArray(items) ? items.length : 0),
    },
    {
      title: "Total",
      dataIndex: "total_cents",
      key: "total_cents",
      // Currency-aware formatting deferred — API currently returns cents without currencyData.
      render: (cents) => (cents != null ? formatPrice(cents / 100) : "\u2014"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        // Pending drafts can be accepted or rejected; accepted drafts can still be rejected
        if (
          record.draft_status !== CATERING_DRAFT_ORDER_STATUS.PENDING &&
          record.draft_status !== CATERING_DRAFT_ORDER_STATUS.ACCEPTED
        )
          return null;

        const isThisRow = activeAction?.orderId === record.orderId;
        return (
          <ActionsContainer>
            {record.draft_status === CATERING_DRAFT_ORDER_STATUS.PENDING && (
              <BbotButton
                type="primary"
                size="small"
                loading={isThisRow && activeAction?.type === "accept"}
                disabled={!!activeAction}
                onClick={() => onAccept(record.orderId)}
              >
                Accept
              </BbotButton>
            )}
            <BbotButton
              danger
              size="small"
              loading={isThisRow && activeAction?.type === "reject"}
              disabled={!!activeAction}
              onClick={() => onReject(record.orderId)}
            >
              Reject
            </BbotButton>
          </ActionsContainer>
        );
      },
    },
  ];
}

const CateringDraftOrdersTab = ({ selectedCustomer }) => {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(false);
  // Tracks in-flight action; while set, ALL action buttons are disabled to prevent concurrent mutations
  const [activeAction, setActiveAction] = useState(null);

  const customerId = selectedCustomer?.customer_id;
  const location = useLocation();
  // Order to scroll to + highlight; sourced from the deep-link query param the
  // merchant-notification email uses. Empty when no highlight is requested.
  const highlightOrderId = useMemo(() => new URLSearchParams(location.search).get("orderId") || "", [location.search]);

  const renderDate = useCallback(
    (date) => {
      if (!date) return "-";
      try {
        const dt = DateTime.fromISO(date);
        const tz = selectedCustomer?.timezone;
        return (tz ? dt.setZone(tz) : dt).toLocaleString(DateTime.DATETIME_SHORT);
      } catch {
        return "-";
      }
    },
    [selectedCustomer?.timezone]
  );

  // A 403 means the customer lacks catering_v2 (or the session has expired).
  // Polling on a hard "not enabled" state would generate a steady stream of
  // error toasts; we trip this flag once and skip subsequent auto-refreshes
  // until the user switches venues.
  const [pollingDisabled, setPollingDisabled] = useState(false);
  useEffect(() => {
    setPollingDisabled(false);
  }, [customerId]);

  const fetchDrafts = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const res = await axios.get(CATERING_DRAFT_ORDERS_ENDPOINT, {
        params: { customer_id: customerId },
      });
      setDrafts(res.data.drafts || []);
    } catch (error) {
      if (error?.response?.status === 403) {
        setPollingDisabled(true);
      }
      generalErrorAlert(error, "Error fetching catering draft orders.", true);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    fetchDrafts();
  }, [fetchDrafts]);

  useEffect(() => {
    if (!customerId || pollingDisabled) return;
    const interval = setInterval(fetchDrafts, AUTO_REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchDrafts, customerId, pollingDisabled]);

  // Scroll the highlighted row into view once it lands in the rendered table.
  useEffect(() => {
    if (!highlightOrderId) return undefined;
    if (!drafts.some((d) => d.orderId === highlightOrderId)) return undefined;
    const timeoutId = window.setTimeout(() => {
      const row = document.querySelector(`[data-row-key="${highlightOrderId}"]`);
      if (row) row.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
    return () => window.clearTimeout(timeoutId);
  }, [highlightOrderId, drafts]);

  const handleAction = useCallback(
    async (orderId, action, reason = "") => {
      if (!customerId) return;
      setActiveAction({ orderId, type: action });
      try {
        await axios.post(CATERING_DRAFT_ORDERS_ENDPOINT, {
          orderId,
          action,
          reason,
          customer_id: customerId,
        });
        notification.success({
          message: `Draft ${ACTION_FORMS[action]?.past ?? action} successfully`,
        });
        await fetchDrafts();
      } catch (error) {
        generalErrorAlert(error, `Error ${ACTION_FORMS[action]?.gerund ?? action} draft.`, true);
      } finally {
        setActiveAction(null);
      }
    },
    [customerId, fetchDrafts]
  );

  // Ref (not state) because Ant Design's Modal.confirm renders content once
  // and does not re-render on state changes.
  const reasonRef = useRef("");

  const confirmReject = useCallback(
    (orderId) => {
      reasonRef.current = "";
      Modal.confirm({
        title: "Reject Catering Draft Order",
        content: (
          <div>
            <p>Are you sure you want to reject this draft? This action cannot be undone.</p>
            <Input.TextArea
              placeholder="Reason for rejection (optional)"
              rows={3}
              maxLength={MAX_REJECT_REASON_LENGTH}
              showCount
              onChange={(e) => {
                reasonRef.current = e.target.value;
              }}
            />
          </div>
        ),
        okText: "Reject",
        okType: "danger",
        onOk: () => handleAction(orderId, "reject", reasonRef.current),
      });
    },
    [handleAction]
  );

  const confirmAccept = useCallback(
    (orderId) => {
      Modal.confirm({
        title: "Accept Catering Draft Order",
        content: "Please verify all items are available before accepting. This confirms the order for the customer.",
        okText: "Accept",
        onOk: () => handleAction(orderId, "accept"),
      });
    },
    [handleAction]
  );

  const columns = useMemo(
    () =>
      buildColumns({
        renderDate,
        activeAction,
        onAccept: confirmAccept,
        onReject: confirmReject,
      }),
    [renderDate, activeAction, confirmAccept, confirmReject]
  );

  return (
    <TableWrapper>
      <HeaderRow>
        <h4>Catering Draft Orders</h4>
        <BbotButton onClick={fetchDrafts} loading={loading}>
          Refresh
        </BbotButton>
      </HeaderRow>
      <BbotTable
        data={drafts}
        manuallyFormattedColumns={columns}
        loading={loading}
        rowKey="orderId"
        rowClassName={(record) => (highlightOrderId && record.orderId === highlightOrderId ? HIGHLIGHT_ROW_CLASS : "")}
        pagination={{ pageSize: 20 }}
        locale={{ emptyText: "No catering draft orders found" }}
      />
    </TableWrapper>
  );
};

export default CateringDraftOrdersTab;

const TableWrapper = styled.div`
  tr.${HIGHLIGHT_ROW_CLASS} td {
    background-color: rgba(250, 219, 20, 0.15);
    transition: background-color 0.5s ease;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const ActionsContainer = styled.div`
  display: flex;
  gap: 8px;
`;

const StatusCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;

const RejecterLabel = styled.div`
  color: rgba(0, 0, 0, 0.45);
  font-size: 12px;
`;
